/**
 * The listings preview of the start dialog (views/new-mobile-map/_start_dialog_listings.php):
 * the switch between browsing the offers and adding a listing shows the part
 * of the chosen mode, and the add mode shows the part of the visitor's account.
 *
 * The server renders all three account parts, the guest's first; the script
 * shows the one of window.RCN_USER and follows `rcn:auth:user-applied`
 * (auth-modal.js). A guest signs in with the form of the panel: it posts to
 * window.RCN_AUTH_URLS.login through the request of the sign-in window
 * (shared/auth-request.js: the page's CSRF token, X-Requested-With), and the
 * endpoint answers with JSON. A confirmed address then opens the listing form;
 * an unconfirmed one is applied to the page (window.RCN_AUTH.applyUser) and
 * the panel says where to confirm it. The password Field shows the password
 * with the button of the account pages (shared/password-toggle.js).
 * „Dodaję ogłoszenie” chosen with a mouse puts the cursor in the e-mail field
 * of a guest. start-dialog.js owns the dialog and lends its helpers. Every
 * sentence comes from the markup.
 */

import { postAuthForm } from '../../shared/auth-request';
import { initPasswordToggle } from '../../shared/password-toggle';

export var ACCOUNT_GUEST = 'gosc';
export var ACCOUNT_UNVERIFIED = 'niepotwierdzony';
export var ACCOUNT_VERIFIED = 'potwierdzony';

// The fields of FrontendLoginForm the panel shows, in the order of the form.
var FIELDS = ['email', 'password'];
// A mouse or a touchpad: there a field takes the cursor without a keyboard
// opening over „Załóż konto”, which a visitor without an account needs.
var FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

/** The account part for a user of window.RCN_USER: null is a guest. */
export function accountState(user) {
    if (!user || typeof user !== 'object') return ACCOUNT_GUEST;
    return user.emailVerified === true ? ACCOUNT_VERIFIED : ACCOUNT_UNVERIFIED;
}

function firstMessage(list) {
    if (Array.isArray(list)) return list.length && typeof list[0] === 'string' ? list[0] : '';
    return typeof list === 'string' ? list : '';
}

/**
 * The messages of a failed sign-in as the panel shows them. FrontendLoginForm
 * returns them by attribute ({email: [...], password: [...]}); wrong
 * credentials come under the password once both fields are filled in, and the
 * panel shows them as one alert, as the sign-in page does (`credentials`).
 * A message of any other attribute goes to the alert too.
 *
 * @param {object|null} errors the `errors` of the JSON answer
 * @param {{email: boolean, password: boolean}} filled which fields were filled in
 * @returns {{fields: Object<string, string>, alert: string, credentials: boolean}}
 */
export function loginErrors(errors, filled) {
    var result = { fields: {}, alert: '', credentials: false };
    if (!errors || typeof errors !== 'object') return result;
    var password = firstMessage(errors.password);
    var email = firstMessage(errors.email);
    if (password && !email && filled && filled.email && filled.password) {
        result.alert = password;
        result.credentials = true;
        return result;
    }
    if (email) result.fields.email = email;
    if (password) result.fields.password = password;
    Object.keys(errors).forEach(function(key) {
        if (FIELDS.indexOf(key) === -1 && !result.alert) result.alert = firstMessage(errors[key]);
    });
    return result;
}

export function initStartDialogListings(root, hooks) {
    var form = root.querySelector('[data-start-login]');
    var alertBox = form ? form.querySelector('[data-start-login-alert]') : null;
    var live = form ? form.querySelector('[data-start-login-live]') : null;
    var submit = form ? form.querySelector('[data-start-login-submit]') : null;
    var idleLabel = submit ? submit.textContent.trim() : '';
    var busy = false;

    function mode() {
        var input = hooks.checked('[data-start-listing-mode]');
        return input ? input.value : '';
    }

    function input(name) {
        return form ? form.querySelector('[data-start-login-field="' + name + '"]') : null;
    }

    // The Field around an input (views/user-account/_field.php): the parent of its label.
    function fieldOf(control) {
        var label = control && control.id ? form.querySelector('label[for="' + control.id + '"]') : null;
        return label ? label.parentNode : null;
    }

    function currentUser() {
        return window.RCN_USER || null;
    }

    /** Shows the account part of `state`; after a sign-in the focus moves to it and it is read out. */
    function showAccount(state, moveFocus) {
        var shown = null;
        root.querySelectorAll('[data-start-listing-account]').forEach(function(part) {
            var current = part.getAttribute('data-start-listing-account') === state;
            part.hidden = !current;
            if (current) shown = part;
        });
        if (!moveFocus || !shown) return;
        var target = shown.querySelector('[data-start-listing-account-focus]');
        if (target && typeof target.focus === 'function') target.focus();
        hooks.announce(target ? target.textContent.replace(/\s+/g, ' ').trim() : '');
    }

    function sync(shouldAnnounce) {
        var current = mode();
        var active = null;
        root.querySelectorAll('[data-start-listing-panel]').forEach(function(panel) {
            var shown = panel.getAttribute('data-start-listing-panel') === current;
            panel.hidden = !shown;
            if (shown) active = panel;
        });
        showAccount(accountState(currentUser()), false);
        if (shouldAnnounce) hooks.announce(hooks.visibleTitle(active));
    }

    function finePointer() {
        return typeof window.matchMedia === 'function' && window.matchMedia(FINE_POINTER_QUERY).matches;
    }

    // „Dodaję ogłoszenie” chosen with a mouse: a guest types the e-mail at
    // once. Keys leave the focus on the switch, so the arrows keep working.
    function focusSignIn() {
        var email = input('email');
        if (!email || typeof email.focus !== 'function' || email.closest('[hidden]')) return;
        if (accountState(currentUser()) === ACCOUNT_GUEST) email.focus();
    }

    function setBusy(on) {
        busy = on;
        if (form) form.setAttribute('aria-busy', on ? 'true' : 'false');
        if (!submit) return;
        // aria-disabled keeps the focus on the button while the request runs.
        submit.classList.toggle('is-loading', on);
        if (on) submit.setAttribute('aria-disabled', 'true');
        else submit.removeAttribute('aria-disabled');
        submit.textContent = on ? (form.getAttribute('data-start-login-busy') || idleLabel) : idleLabel;
    }

    function clearErrors() {
        if (!form) return;
        form.querySelectorAll('[data-start-login-field-error]').forEach(function(node) { node.remove(); });
        FIELDS.forEach(function(name) {
            var control = input(name);
            if (!control) return;
            control.removeAttribute('aria-invalid');
            control.removeAttribute('aria-describedby');
            var field = fieldOf(control);
            if (field) field.classList.remove('has-error');
        });
        if (alertBox) {
            alertBox.textContent = '';
            alertBox.hidden = true;
        }
        if (live) live.textContent = '';
    }

    function markInvalid(control, describedBy) {
        control.setAttribute('aria-invalid', 'true');
        control.setAttribute('aria-describedby', describedBy);
        var field = fieldOf(control);
        if (field) field.classList.add('has-error');
    }

    function setFieldError(control, message) {
        var field = fieldOf(control);
        if (!field) return;
        // The error line of the shared Field, as views/user-account/_field.php renders it after a reload.
        var error = document.createElement('span');
        var id = control.id + '-error';
        error.className = 'ms-field__error';
        error.setAttribute('id', id);
        error.setAttribute('data-start-login-field-error', '');
        error.textContent = message;
        field.appendChild(error);
        markInvalid(control, id);
    }

    function showAlert(message) {
        if (!alertBox) return;
        alertBox.textContent = message;
        alertBox.hidden = false;
    }

    /** Inline errors under the fields, the alert, the live region and the focus on the first error. */
    function showErrors(result) {
        var first = null;
        FIELDS.forEach(function(name) {
            var control = input(name);
            if (control && result.fields[name]) {
                setFieldError(control, result.fields[name]);
                first = first || control;
            }
        });
        if (result.alert) showAlert(result.alert);
        var password = input('password');
        if (result.credentials && password && alertBox) {
            markInvalid(password, alertBox.id);
            first = password;
        }
        if (live) live.textContent = [result.alert, result.fields.email, result.fields.password].filter(Boolean).join(' ');
        var target = first || alertBox;
        if (target && typeof target.focus === 'function') target.focus();
        if (first && result.credentials && typeof first.select === 'function') first.select();
    }

    function failed(attribute) {
        setBusy(false);
        showErrors({ fields: {}, alert: (form && form.getAttribute(attribute)) || '', credentials: false });
    }

    function goTo(url) {
        var location = window.location;
        if (location && typeof location.assign === 'function') location.assign(url);
        else if (location) location.href = url;
    }

    function signedIn(user) {
        if (accountState(user) === ACCOUNT_VERIFIED) {
            // The page leaves for the listing form: the button stays busy.
            goTo((form && form.getAttribute('data-start-login-next')) || '/');
            return;
        }
        setBusy(false);
        var password = input('password');
        if (password) password.value = '';
        // The sign-in window updates the top bar and tells the page; without it the panel keeps the user itself.
        var auth = window.RCN_AUTH;
        try {
            if (auth && typeof auth.applyUser === 'function') auth.applyUser(user);
            else window.RCN_USER = user;
        } catch (e) {
            window.RCN_USER = user;
        }
        showAccount(accountState(user), true);
    }

    // The fields as the browser would send the form; postAuthForm adds the page's CSRF token.
    function body() {
        var params = new URLSearchParams();
        form.querySelectorAll('input').forEach(function(control) {
            var name = control.getAttribute('name');
            var type = control.getAttribute('type');
            if (!name || control.disabled) return;
            if ((type === 'checkbox' || type === 'radio') && !control.checked) return;
            params.append(name, control.value);
        });
        return params;
    }

    function signIn() {
        if (busy || !form) return;
        var url = (window.RCN_AUTH_URLS && window.RCN_AUTH_URLS.login) || form.getAttribute('action');
        var filled = {};
        FIELDS.forEach(function(name) {
            var control = input(name);
            filled[name] = Boolean(control && String(control.value).trim() !== '');
        });
        var params = body();
        clearErrors();
        setBusy(true);
        postAuthForm(url, params)
            .then(function(result) {
                var payload = result.payload || {};
                if (result.ok && payload.success && payload.user) {
                    signedIn(payload.user);
                    return;
                }
                var errors = loginErrors(payload.errors, filled);
                if (!errors.alert && !errors.fields.email && !errors.fields.password) {
                    failed('data-start-login-error');
                    return;
                }
                setBusy(false);
                showErrors(errors);
            })
            .catch(function() {
                failed('data-start-login-failed');
            });
    }

    if (form) initPasswordToggle(form.querySelector('[data-password-toggle]'), input('password'));

    var modeByPointer = false;
    root.addEventListener('pointerdown', function(event) {
        var target = event.target;
        modeByPointer = Boolean(target && target.closest && target.closest('[data-start-listing-modes]'));
    });
    // A key after a click on the mode already chosen is a key choice again.
    root.addEventListener('keydown', function() { modeByPointer = false; });

    root.addEventListener('change', function(event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches('[data-start-listing-mode]')) return;
        sync(true);
        if (modeByPointer && finePointer()) focusSignIn();
        modeByPointer = false;
    });

    root.addEventListener('submit', function(event) {
        if (!form || event.target !== form) return;
        event.preventDefault();
        signIn();
    });

    // A sign-in in the window of the top bar, or here: the panel follows the account.
    document.addEventListener('rcn:auth:user-applied', function(event) {
        var detail = event && event.detail;
        var user = detail && typeof detail === 'object' && 'user' in detail ? detail.user : currentUser();
        showAccount(accountState(user), false);
    });

    showAccount(accountState(currentUser()), false);

    return { sync: sync };
}
